import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Snackbar, Alert, Box, Typography, Button, Chip, CircularProgress } from '@mui/material';
import OptimizationPanel from '../Optimizations/OptimizationPanel';
import axios from 'axios';

function UnifiedDashboard() {
  const navigate = useNavigate();
  
  // Estados para campanhas - separados por plataforma
  const [metaCampaigns, setMetaCampaigns] = useState([]);
  const [googleCampaigns, setGoogleCampaigns] = useState([]);
  const [googleConnected, setGoogleConnected] = useState(false);
  
  // Estados de controle
  const [stats, setStats] = useState({
    total: 0,
    meta: 0,
    google: 0,
    linkedin: 0,
    active: 0,
    simulation: 0
  });
  const [loading, setLoading] = useState(true);
  const [loadingGoogle, setLoadingGoogle] = useState(false);
  const [error, setError] = useState('');
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  
  // Estados para Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  // Função para mostrar Snackbar
  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  }, []);

  // Função para fechar Snackbar
  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };
  
  // ✅ CORRIGIDO: Carregar campanhas do Google Ads com melhor tratamento de erros
  const loadGoogleCampaigns = useCallback(async () => {
    const googleCustomerId = localStorage.getItem('google_customer_id');
    const googleRefreshToken = localStorage.getItem('google_refresh_token');
    
    // Verificar se está conectado ao Google Ads
    if (!googleCustomerId || !googleRefreshToken) {
      setGoogleConnected(false);
      setGoogleCampaigns([]);
      return;
    }
    
    setGoogleConnected(true);
    setLoadingGoogle(true);
    
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/google-ads/campaigns`,
        { 
          params: { 
            customerId: googleCustomerId,
            refreshToken: googleRefreshToken 
          },
          timeout: 10000 // Timeout de 10 segundos
        }
      );
      
      const campaigns = response.data.campaigns || [];
      setGoogleCampaigns(campaigns.map(c => ({ 
        ...c, 
        platform: 'Google Ads' 
      })));
      
      if (campaigns.length > 0) {
        showSnackbar(`${campaigns.length} campanhas Google Ads carregadas`, 'success');
      }
      
    } catch (err) {
      console.error('Erro ao buscar Google Ads:', err);
      
      // Tratamento específico de erros
      if (err.code === 'ECONNABORTED') {
        showSnackbar('Timeout ao carregar Google Ads', 'warning');
      } else if (err.response?.status === 401) {
        showSnackbar('Sessão Google Ads expirada. Reconecte sua conta.', 'error');
        setGoogleConnected(false);
        localStorage.removeItem('google_customer_id');
        localStorage.removeItem('google_refresh_token');
      } else if (err.response?.status === 404) {
        // Endpoint não existe ainda - silencioso
        console.log('Endpoint Google Ads não disponível');
      } else {
        showSnackbar('Erro ao carregar Google Ads', 'error');
      }
      
      setGoogleCampaigns([]);
    } finally {
      setLoadingGoogle(false);
    }
  }, [showSnackbar]);
  
  // ✅ CORRIGIDO: Carregar campanhas do Meta Ads com melhor tratamento
  const loadMetaCampaigns = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        console.warn('Token não encontrado');
        setMetaCampaigns([]);
        return;
      }
      
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/campaigns`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000 // Timeout de 10 segundos
        }
      );
      
      const campaigns = response.data.campaigns || [];
      setMetaCampaigns(campaigns.map(c => ({ 
        ...c, 
        platform: 'Meta Ads' 
      })));
      
      if (campaigns.length > 0) {
        console.log(`${campaigns.length} campanhas Meta carregadas`);
      }
      
    } catch (err) {
      console.error('Erro ao buscar Meta Ads:', err);
      
      // Tratamento específico de erros
      if (err.code === 'ECONNABORTED') {
        showSnackbar('Timeout ao carregar Meta Ads', 'warning');
      } else if (err.response?.status === 401) {
        showSnackbar('Sessão expirada. Faça login novamente.', 'error');
        // Redirecionar para login após 2 segundos
        setTimeout(() => navigate('/login'), 2000);
      } else if (err.response?.status === 404) {
        console.log('Endpoint Meta Ads não disponível');
      } else {
        showSnackbar('Erro ao carregar Meta Ads', 'error');
      }
      
      setMetaCampaigns([]);
      throw err; // Propagar erro para loadAllCampaigns
    }
  }, [showSnackbar, navigate]);
  
  // ✅ CORRIGIDO: Carregar todas as campanhas com melhor controle
  const loadAllCampaigns = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Carregar Meta e Google em paralelo, mas não falhar se um deles falhar
      await Promise.allSettled([
        loadMetaCampaigns(),
        loadGoogleCampaigns()
      ]);
      
    } catch (err) {
      console.error('Erro ao carregar campanhas:', err);
      setError('Erro ao carregar algumas campanhas. Algumas funcionalidades podem estar limitadas.');
    } finally {
      setLoading(false);
    }
  }, [loadMetaCampaigns, loadGoogleCampaigns]);
  
  // Calcular estatísticas quando campanhas mudarem
  useEffect(() => {
    const allCampaigns = [...metaCampaigns, ...googleCampaigns];
    
    setStats({
      total: allCampaigns.length,
      meta: metaCampaigns.length,
      google: googleCampaigns.length,
      linkedin: 0,
      active: allCampaigns.filter(c => 
        c.status === 'active' || 
        c.status === 'ACTIVE' || 
        c.status === 'ENABLED'
      ).length,
      simulation: allCampaigns.filter(c => c.status === 'simulation').length
    });
  }, [metaCampaigns, googleCampaigns]);
  
  // Carregar campanhas ao montar componente
  useEffect(() => {
    loadAllCampaigns();
  }, [loadAllCampaigns]);
  
  // Funções auxiliares
  const getPlatformIcon = (platform) => {
    const plat = (platform || '').toLowerCase();
    if (plat.includes('meta')) return '📘';
    if (plat.includes('google')) return '🎯';
    if (plat.includes('linkedin')) return '💼';
    return '📊';
  };
  
  const getPlatformName = (platform) => {
    const plat = (platform || '').toLowerCase();
    if (plat.includes('meta')) return 'Meta Ads';
    if (plat.includes('google')) return 'Google Ads';
    if (plat.includes('linkedin')) return 'LinkedIn Ads';
    return 'Outra Plataforma';
  };
  
  const getStatusColor = (status) => {
    const statusLower = (status || '').toLowerCase();
    if (statusLower === 'active' || statusLower === 'enabled') return '#4caf50';
    if (statusLower === 'paused') return '#ff9800';
    if (statusLower === 'simulation') return '#2196f3';
    if (statusLower === 'rascunho') return '#757575';
    return '#757575';
  };
  
  const getStatusLabel = (status) => {
    const statusLower = (status || '').toLowerCase();
    if (statusLower === 'active' || statusLower === 'enabled') return 'Ativa';
    if (statusLower === 'paused') return 'Pausada';
    if (statusLower === 'simulation') return 'Simulação';
    if (statusLower === 'rascunho') return 'Rascunho';
    return status || 'Desconhecido';
  };

  const handleCloseOptimizationPanel = () => {
    setSelectedCampaign(null);
  };

  const handleOptimizeCampaign = (campaign) => {
    setSelectedCampaign(campaign);
    showSnackbar(`Abrindo otimizações para ${campaign.name}`, 'info');
  };

  // Função para criar campanha Meta Ads
  const handleCreateMetaCampaign = () => {
    showSnackbar('Redirecionando para criar campanha Meta...', 'info');
    navigate('/meta/campaigns');
  };

  // ✅ CORRIGIDO: Ativar campanha com melhor tratamento de erros
  const handleActivateCampaign = async (campaign) => {
    try {
      setLoading(true);
      
      if (campaign.platform === 'Meta Ads') {
        await axios.patch(
          `${process.env.REACT_APP_API_URL}/api/campaigns/${campaign.id}/status`,
          { status: 'ACTIVE' },
          { 
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            timeout: 10000
          }
        );
      } else if (campaign.platform === 'Google Ads') {
        await axios.patch(
          `${process.env.REACT_APP_API_URL}/api/google-ads/campaigns/${campaign.id}/status`,
          { 
            status: 'ENABLED',
            customerId: localStorage.getItem('google_customer_id'),
            refreshToken: localStorage.getItem('google_refresh_token')
          },
          { timeout: 10000 }
        );
      }
      
      showSnackbar('Campanha ativada com sucesso!', 'success');
      await loadAllCampaigns();
      
    } catch (error) {
      console.error('Erro ao ativar:', error);
      
      if (error.response?.status === 404) {
        showSnackbar('Campanha não encontrada', 'error');
      } else if (error.response?.status === 401) {
        showSnackbar('Sessão expirada. Faça login novamente.', 'error');
      } else {
        showSnackbar('Erro ao ativar campanha', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  // ✅ CORRIGIDO: Pausar campanha com melhor tratamento de erros
  const handlePauseCampaign = async (campaign) => {
    try {
      setLoading(true);
      
      if (campaign.platform === 'Meta Ads') {
        await axios.patch(
          `${process.env.REACT_APP_API_URL}/api/campaigns/${campaign.id}/status`,
          { status: 'PAUSED' },
          { 
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            timeout: 10000
          }
        );
      } else if (campaign.platform === 'Google Ads') {
        await axios.patch(
          `${process.env.REACT_APP_API_URL}/api/google-ads/campaigns/${campaign.id}/status`,
          { 
            status: 'PAUSED',
            customerId: localStorage.getItem('google_customer_id'),
            refreshToken: localStorage.getItem('google_refresh_token')
          },
          { timeout: 10000 }
        );
      }
      
      showSnackbar('Campanha pausada com sucesso!', 'success');
      await loadAllCampaigns();
      
    } catch (error) {
      console.error('Erro ao pausar:', error);
      
      if (error.response?.status === 404) {
        showSnackbar('Campanha não encontrada', 'error');
      } else if (error.response?.status === 401) {
        showSnackbar('Sessão expirada. Faça login novamente.', 'error');
      } else {
        showSnackbar('Erro ao pausar campanha', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  // Componente de card de campanha
  const CampaignCard = ({ campaign }) => (
    <div style={{ 
      background: '#f8f9fa', 
      border: '1px solid #e0e0e0', 
      borderRadius: '10px', 
      padding: '20px',
      transition: 'transform 0.2s, box-shadow 0.2s',
      cursor: 'pointer'
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = 'translateY(-4px)';
      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = 'none';
    }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '12px' 
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '20px' }}>
            {getPlatformIcon(campaign.platform)}
          </span>
          <span style={{ fontSize: '13px', color: '#666', fontWeight: '500' }}>
            {getPlatformName(campaign.platform)}
          </span>
        </div>
        <span style={{
          padding: '4px 12px',
          borderRadius: '20px',
          fontSize: '12px',
          color: 'white',
          fontWeight: '600',
          backgroundColor: getStatusColor(campaign.status)
        }}>
          {getStatusLabel(campaign.status)}
        </span>
      </div>
      
      <h3 style={{ 
        margin: '0 0 16px 0', 
        fontSize: '18px', 
        color: '#333', 
        fontWeight: '600' 
      }}>
        {campaign.name}
      </h3>
      
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          fontSize: '13px' 
        }}>
          <span style={{ color: '#666', fontWeight: '500' }}>💰 Orçamento:</span>
          <span style={{ color: '#333', fontWeight: '600' }}>
            R$ {campaign.budget || campaign.orcamento || 0}/dia
          </span>
        </div>
        
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          fontSize: '13px' 
        }}>
          <span style={{ color: '#666', fontWeight: '500' }}>📅 Criada em:</span>
          <span style={{ color: '#333', fontWeight: '600' }}>
            {campaign.created_at ? new Date(campaign.created_at).toLocaleDateString('pt-BR') : 'N/A'}
          </span>
        </div>
        
        <button 
          onClick={() => handleOptimizeCampaign(campaign)}
          style={{
            marginTop: '12px',
            padding: '8px 16px',
            background: '#2196f3',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'background 0.3s',
            width: '100%'
          }}
          onMouseOver={(e) => e.target.style.background = '#1976d2'}
          onMouseOut={(e) => e.target.style.background = '#2196f3'}
        >
          🚀 Otimizar Campanha
        </button>

        {(campaign.status === 'paused' || campaign.status === 'PAUSED') && (
          <button
            onClick={() => handleActivateCampaign(campaign)}
            disabled={loading}
            style={{
              marginTop: '8px',
              padding: '8px 16px',
              background: loading ? '#cccccc' : '#4caf50',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer',
              width: '100%'
            }}
          >
            {loading ? '⏳ Ativando...' : '▶️ Ativar Campanha'}
          </button>
        )}

        {(campaign.status === 'active' || campaign.status === 'ACTIVE' || campaign.status === 'ENABLED') && (
          <button
            onClick={() => handlePauseCampaign(campaign)}
            disabled={loading}
            style={{
              marginTop: '8px',
              padding: '8px 16px',
              background: loading ? '#cccccc' : '#ff9800',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer',
              width: '100%'
            }}
          >
            {loading ? '⏳ Pausando...' : '⏸️ Pausar Campanha'}
          </button>
        )}
      </div>
    </div>
  );
  
  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '30px', 
        flexWrap: 'wrap', 
        gap: '15px' 
      }}>
        <h1 style={{ margin: 0, fontSize: '28px', color: '#333' }}>
          Dashboard Unificado - Growly
        </h1>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button 
            onClick={handleCreateMetaCampaign}
            style={{
              padding: '12px 24px',
              background: '#1877f2',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'background 0.3s'
            }}
            onMouseOver={(e) => e.target.style.background = '#1565c0'}
            onMouseOut={(e) => e.target.style.background = '#1877f2'}
          >
            📘 Nova Campanha Meta Ads
          </button>
          <button 
            onClick={() => {
              showSnackbar('Redirecionando para criar campanha Google...', 'info');
              setTimeout(() => navigate('/google-ads'), 500);
            }}
            style={{
              padding: '12px 24px',
              background: '#ea4335',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'background 0.3s'
            }}
            onMouseOver={(e) => e.target.style.background = '#d33426'}
            onMouseOut={(e) => e.target.style.background = '#ea4335'}
          >
            🎯 Nova Campanha Google Ads
          </button>
        </div>
      </div>

      {/* ✅ Alert de erro global */}
      {error && (
        <div style={{ 
          background: '#ffebee', 
          border: '1px solid #f44336', 
          borderRadius: '8px', 
          padding: '16px', 
          marginBottom: '20px' 
        }}>
          <span style={{ fontSize: '18px', marginRight: '10px' }}>⚠️</span>
          <span style={{ color: '#c62828' }}>{error}</span>
        </div>
      )}
      
      {/* Cards de Estatísticas */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
        gap: '20px', 
        marginBottom: '30px' 
      }}>
        <div style={{ 
          background: 'white', 
          borderRadius: '12px', 
          padding: '24px', 
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)' 
        }}>
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>
            Total de Campanhas
          </div>
          <div style={{ fontSize: '32px', fontWeight: '700', color: '#333' }}>
            {stats.total}
          </div>
        </div>
        
        <div style={{ 
          background: 'white', 
          borderRadius: '12px', 
          padding: '24px', 
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)' 
        }}>
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>
            Campanhas Ativas
          </div>
          <div style={{ fontSize: '32px', fontWeight: '700', color: '#4caf50' }}>
            {stats.active}
          </div>
        </div>
        
        <div style={{ 
          background: 'white', 
          borderRadius: '12px', 
          padding: '24px', 
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)' 
        }}>
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>
            Meta Ads
          </div>
          <div style={{ fontSize: '32px', fontWeight: '700', color: '#1877f2' }}>
            {stats.meta}
          </div>
        </div>
        
        <div style={{ 
          background: 'white', 
          borderRadius: '12px', 
          padding: '24px', 
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)' 
        }}>
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>
            Google Ads
          </div>
          <div style={{ fontSize: '32px', fontWeight: '700', color: '#ea4335' }}>
            {stats.google}
          </div>
        </div>
      </div>
      
      {/* Alert para modo simulação */}
      {stats.simulation > 0 && (
        <div style={{ 
          background: '#fff3cd', 
          border: '1px solid #ffc107', 
          borderRadius: '8px', 
          padding: '16px', 
          marginBottom: '30px' 
        }}>
          <span style={{ fontSize: '18px', marginRight: '10px' }}>⚠️</span>
          <span style={{ color: '#856404' }}>
            Você tem {stats.simulation} campanha(s) em modo simulação
          </span>
        </div>
      )}

      {/* Status de conexão Google Ads */}
      {!googleConnected && (
        <div style={{
          background: '#e3f2fd',
          border: '1px solid #2196f3',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '30px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '10px'
        }}>
          <div>
            <span style={{ fontSize: '18px', marginRight: '10px' }}>ℹ️</span>
            <span style={{ color: '#1565c0' }}>
              Conecte sua conta Google Ads para gerenciar campanhas
            </span>
          </div>
          <button
            onClick={() => navigate('/google-ads/connect')}
            style={{
              padding: '8px 16px',
              background: '#2196f3',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'background 0.3s'
            }}
          >
            Conectar Google Ads
          </button>
        </div>
      )}
      
      {/* Painel de Otimizações */}
      {selectedCampaign && (
        <OptimizationPanel 
          campaignId={selectedCampaign.id}
          campaignName={selectedCampaign.name}
          onClose={handleCloseOptimizationPanel}
        />
      )}
      
      {/* SEÇÃO META ADS */}
      <div style={{ 
        background: 'white', 
        borderRadius: '12px', 
        padding: '24px', 
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        marginBottom: '30px'
      }}>
        <h2 style={{ 
          margin: '0 0 24px 0', 
          fontSize: '20px', 
          color: '#333',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          flexWrap: 'wrap'
        }}>
          <span style={{ fontSize: '24px' }}>📘</span>
          Campanhas Meta Ads
          <Chip 
            label={metaCampaigns.length} 
            size="small" 
            color="primary"
            style={{ background: '#1877f2' }}
          />
        </h2>
        
        {loading && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>Carregando campanhas Meta...</Typography>
          </Box>
        )}
        
        {!loading && metaCampaigns.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="textSecondary" paragraph>
              Nenhuma campanha Meta Ads encontrada
            </Typography>
            <Button 
              variant="contained"
              onClick={handleCreateMetaCampaign}
              sx={{ background: '#1877f2', '&:hover': { background: '#1565c0' } }}
            >
              Criar Campanha Meta Ads
            </Button>
          </Box>
        )}
        
        {!loading && metaCampaigns.length > 0 && (
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', 
            gap: '20px' 
          }}>
            {metaCampaigns.map((campaign) => (
              <CampaignCard key={campaign.id} campaign={campaign} />
            ))}
          </div>
        )}
      </div>

      {/* SEÇÃO GOOGLE ADS */}
      <div style={{ 
        background: 'white', 
        borderRadius: '12px', 
        padding: '24px', 
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <h2 style={{ 
          margin: '0 0 24px 0', 
          fontSize: '20px', 
          color: '#333',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          flexWrap: 'wrap'
        }}>
          <span style={{ fontSize: '24px' }}>🎯</span>
          Campanhas Google Ads
          <Chip 
            label={googleCampaigns.length} 
            size="small" 
            color="error"
            style={{ background: '#ea4335' }}
          />
          {loadingGoogle && <CircularProgress size={20} />}
        </h2>
        
        {!googleConnected && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="textSecondary" paragraph>
              Conecte sua conta Google Ads para visualizar suas campanhas
            </Typography>
            <Button 
              variant="contained"
              onClick={() => navigate('/google-ads/connect')}
              sx={{ background: '#ea4335', '&:hover': { background: '#d33426' } }}
            >
              Conectar Google Ads
            </Button>
          </Box>
        )}
        
        {googleConnected && loadingGoogle && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>Carregando campanhas Google Ads...</Typography>
          </Box>
        )}
        
        {googleConnected && !loadingGoogle && googleCampaigns.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="textSecondary" paragraph>
              Nenhuma campanha Google Ads encontrada
            </Typography>
            <Button 
              variant="contained"
              onClick={() => navigate('/google-ads')}
              sx={{ background: '#ea4335', '&:hover': { background: '#d33426' } }}
            >
              Criar Campanha Google Ads
            </Button>
          </Box>
        )}
        
        {googleConnected && !loadingGoogle && googleCampaigns.length > 0 && (
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', 
            gap: '20px' 
          }}>
            {googleCampaigns.map((campaign) => (
              <CampaignCard key={campaign.id} campaign={campaign} />
            ))}
          </div>
        )}
      </div>

      {/* Botão de recarregar */}
      <div style={{ textAlign: 'center', marginTop: '30px' }}>
        <button
          onClick={loadAllCampaigns}
          disabled={loading}
          style={{
            padding: '12px 32px',
            background: loading ? '#cccccc' : '#2196f3',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: loading ? 'not-allowed' : 'pointer',
            transition: 'background 0.3s'
          }}
        >
          {loading ? '⏳ Carregando...' : '🔄 Recarregar Campanhas'}
        </button>
      </div>
    </div>
  );
}

export default UnifiedDashboard;